/**
 * 战争迷雾象棋的类型契约。
 *
 * 本文件是纯类型声明与常量，不包含任何副作用，也不依赖 React / DOM。
 */

import type { Pos } from '@games/core'

/** 棋子颜色。`r` = 红方（下方，先行），`b` = 黑方（上方）。 */
export type Color = 'r' | 'b'

/** 棋子类型：K 将/帅、A 士/仕、B 象/相、N 马、R 车、C 炮、P 卒/兵。 */
export type PieceType = 'K' | 'A' | 'B' | 'N' | 'R' | 'C' | 'P'

/** 一枚棋子。 */
export interface Piece {
  readonly color: Color
  readonly type: PieceType
}

/** 棋盘：`board[r][c]`，`null` 表示空格。 */
export type Board = ReadonlyArray<ReadonlyArray<Piece | null>>

/** 一步走法。 */
export interface Move {
  readonly from: Pos
  readonly to: Pos
}

/** 一局战争迷雾象棋的全部内部状态。 */
export interface FogChessState {
  readonly board: Board
  readonly turn: Color
  readonly winner: Color | null
  readonly moveCount: number
  readonly lastMove: Move | null
  /** captured['r'] = 红方被吃掉的棋子（按被吃顺序） */
  readonly captured: Readonly<Record<Color, readonly PieceType[]>>
}

/** 单个玩家能看到的视图。 */
export interface FogChessPlayerView {
  readonly kind: 'player'
  readonly self: Color
  /** true 表示黑方视角，棋盘需要 180° 旋转，让己方棋子显示在下方 */
  readonly flip: boolean
  readonly turn: Color
  readonly winner: Color | null
  /** 逻辑坐标（未旋转）的棋盘 */
  readonly board: Board
  /** 己方可见格的键集合，键格式 `${r}_${c}`；含己方棋子所在格与所有可走/可见目标 */
  readonly visible: readonly string[]
  readonly lastMove: Move | null
  readonly capturedMine: readonly PieceType[]
  readonly capturedOpp: readonly PieceType[]
  /** 非本方回合、或已分出胜负、或对手断线时为 true */
  readonly disabled: boolean
}

/**
 * 观众视角的三种模式（D9）。
 * - `god`：上帝视角，完整棋盘 + 双方视野遮罩；
 * - `red` / `black`：代入某一方，只看到该方能看到的格子（与那位玩家本人的画面一致）。
 */
export type SpectateMode = 'god' | 'red' | 'black'

/**
 * 观战者看到的视图：**上帝视角**，即整块棋盘（含双方棋子）+ 双方「看不到」的格子。
 *
 * 只给出「不可见集合」而不给出任何一方视角的裁剪结果，是因为三种观察模式（D9）
 * 都能从这同一份数据推出来，切换只发生在视图组件的本地状态里。
 */
export interface FogChessSpectatorView {
  readonly kind: 'spectator'
  /** 逻辑坐标（未旋转）的完整棋盘 */
  readonly board: Board
  readonly turn: Color
  readonly winner: Color | null
  readonly lastMove: Move | null
  /** 红方看不到的格子（键格式 `${r}_${c}`）→ 渲染红色遮罩 */
  readonly hiddenFromRed: readonly string[]
  /** 黑方看不到的格子 → 渲染深灰色遮罩 */
  readonly hiddenFromBlack: readonly string[]
  /** 观众永远不能走子 */
  readonly disabled: true
}

/** 对局视图：玩家视角或观众视角。 */
export type FogChessView = FogChessPlayerView | FogChessSpectatorView

/** 棋盘上的一层遮罩：某一方看不到的格子。 */
export interface BoardMask {
  readonly color: Color
  readonly cells: ReadonlySet<string>
}

/**
 * 棋盘渲染的统一输入：玩家视图与观众视图都先归一成它，
 * `Board` 因此完全不需要知道「自己现在服务的是玩家还是观众」。
 */
export interface BoardPresentation {
  readonly board: Board
  readonly flip: boolean
  /** 己方颜色；观众为 `null`，此时永久不可走子 */
  readonly self: Color | null
  /** 可见格键集合；`null` = 全部可见（上帝视角，不画迷雾） */
  readonly visible: ReadonlySet<string> | null
  readonly lastMove: Move | null
  readonly winner: Color | null
  /** 要盖在棋子**之上**的遮罩（观众双视野用） */
  readonly masks: readonly BoardMask[]
}

/** 棋盘行数。 */
export const ROWS = 10
/** 棋盘列数。 */
export const COLS = 9

/** 逻辑坐标 → 可见性格键。 */
export const cellKey = (r: number, c: number): string => `${r}_${c}`